import React, { useMemo, useState } from "react";

import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { COMPARE_METRICS, RankedMetricDef } from "consts";

import { ValidationRow } from "models";

import { AntModal } from "components";

import { ChartTooltipProps } from "pages/admin/soilSight/utils/compareTypes.ts";
import { getScenarioColor } from "pages/admin/soilSight/utils/compareUtils.ts";

type DeltaTooltipProps = ChartTooltipProps<number> & { metric: RankedMetricDef };

const DeltaTooltip = ({ active, payload, label, metric }: DeltaTooltipProps) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="soil-sight-tooltip">
      <div className="soil-sight-tooltip-year">Year {label}</div>
      {payload.map((entry) => (
        <div key={entry.name} className="soil-sight-tooltip-row">
          <span className="soil-sight-tooltip-dot" style={{ background: entry.color ?? "#999" }} />
          <span className="soil-sight-tooltip-name">{entry.name}</span>
          <span className="soil-sight-tooltip-value">
            {entry.value != null ? `${Number(entry.value) > 0 ? "+" : ""}${Number(entry.value).toFixed(2)} ${metric.unit}` : "—"}
          </span>
        </div>
      ))}
    </div>
  );
};

const CompareChartContent = ({
  metric,
  chartData,
  scenarios,
  height,
  fontSize,
}: {
  metric: RankedMetricDef;
  chartData: Record<string, number | string>[];
  scenarios: string[];
  height: number;
  fontSize: number;
}) => (
  <ResponsiveContainer width="100%" height={height}>
    <LineChart data={chartData} margin={{ top: 4, right: 16, bottom: 0, left: -8 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
      <XAxis dataKey="year" tick={{ fontSize }} label={{ value: "Year", position: "insideBottomRight", offset: -4, fontSize }} />
      <YAxis tick={{ fontSize }} />
      <ReferenceLine y={0} stroke="#9ca3af" strokeDasharray="4 2" />
      <Tooltip content={<DeltaTooltip metric={metric} />} />
      {scenarios.map((sc, i) => (
        <Line key={sc} type="monotone" dataKey={`${sc}__${metric.key}`} stroke={getScenarioColor(sc, i)} strokeWidth={2} dot={false} name={sc} />
      ))}
    </LineChart>
  </ResponsiveContainer>
);

export const MultipleVersionCharts = ({ csvA, csvB }: { csvA: ValidationRow[]; csvB: ValidationRow[] }) => {
  const locations = useMemo(() => [...new Set(csvB.map((r) => r.location))].sort(), [csvB]);
  const rotations = useMemo(() => [...new Set(csvB.map((r) => r.rotation))].sort(), [csvB]);
  const rotationCrops = useMemo(() => {
    const m = new Map<string, string>();
    for (const row of csvB) if (typeof row.rotationCrops === "string") m.set(row.rotation, row.rotationCrops);
    return m;
  }, [csvB]);
  const scenarios = useMemo(() => [...new Set(csvB.map((r) => r.scenario))].sort(), [csvB]);

  const [location, setLocation] = useState(locations[0] ?? "");
  const [rotation, setRotation] = useState(rotations[0] ?? "");
  const [zoomed, setZoomed] = useState<RankedMetricDef | null>(null);

  const indexA = useMemo(() => {
    const m = new Map<string, ValidationRow>();
    for (const row of csvA) m.set(`${row.location}|${row.rotation}|${row.scenario}|${row.year}`, row);
    return m;
  }, [csvA]);

  const chartData = useMemo(() => {
    const maxYear = Math.max(...csvB.map((r) => r.year));
    return Array.from({ length: maxYear }, (_, i) => {
      const year = i + 1;
      const point: Record<string, number | string> = { year };
      for (const scenario of scenarios) {
        const rowB = csvB.find((r) => r.location === location && r.rotation === rotation && r.scenario === scenario && r.year === year);
        const rowA = rowB ? indexA.get(`${location}|${rotation}|${scenario}|${year}`) : undefined;
        if (rowB && rowA) {
          for (const compareMetric of COMPARE_METRICS) {
            const valA = rowA[compareMetric.key];
            const valB = rowB[compareMetric.key];
            if (typeof valA === "number" && typeof valB === "number") {
              point[`${scenario}__${compareMetric.key}`] = valB - valA;
            }
          }
        }
      }
      return point;
    });
  }, [csvB, location, rotation, scenarios, indexA]);

  return (
    <div>
      <div className="soil-sight-charts-toolbar">
        <select value={location} onChange={(e) => setLocation(e.target.value)} className="soil-sight-native-select" style={{ minWidth: 140 }}>
          {locations.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>
        <select value={rotation} onChange={(e) => setRotation(e.target.value)} className="soil-sight-native-select" style={{ minWidth: 260 }}>
          {rotations.map((r) => (
            <option key={r} value={r}>
              {rotationCrops.get(r) ? `${r} (${rotationCrops.get(r)})` : r}
            </option>
          ))}
        </select>
        <div className="soil-sight-charts-legend">
          {scenarios.map((sc, i) => (
            <span key={sc} className="soil-sight-charts-legend-item">
              <span className="soil-sight-charts-legend-swatch" style={{ background: getScenarioColor(sc, i) }} />
              {sc}
            </span>
          ))}
        </div>
      </div>
      <div className="soil-sight-charts-hint">Delta per year (B minus A). Dashed line = zero. Green direction = improvement.</div>

      <div className="soil-sight-charts-grid">
        {COMPARE_METRICS.map((metric) => (
          <div
            key={metric.key}
            onClick={() => setZoomed(metric)}
            className="soil-sight-chart-card"
            onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.10)")}
            onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
          >
            <div className="soil-sight-chart-card-header">
              {metric.label}
              <span className="soil-sight-chart-unit-badge">{metric.unit}</span>
              <span className={`soil-sight-chart-direction-badge soil-sight-chart-direction-badge--${metric.higherIsBetter ? "higher" : "lower"}`}>
                {metric.higherIsBetter ? "↑ higher" : "↓ lower"}
              </span>
            </div>
            <CompareChartContent metric={metric} chartData={chartData} scenarios={scenarios} height={180} fontSize={9} />
          </div>
        ))}
      </div>

      <AntModal
        open={!!zoomed}
        onCancel={() => setZoomed(null)}
        afterOpenChange={(open) => {
          if (open) window.dispatchEvent(new Event("resize"));
        }}
        footer={null}
        width="80vw"
        title={
          zoomed ? (
            <div>
              <div className="soil-sight-modal-title-row">
                {zoomed.label}
                <span className="soil-sight-modal-unit-badge">{zoomed.unit}</span>
                <span className={`soil-sight-modal-direction-badge soil-sight-modal-direction-badge--${zoomed.higherIsBetter ? "higher" : "lower"}`}>
                  {zoomed.higherIsBetter ? "↑ higher is better" : "↓ lower is better"}
                </span>
              </div>
              <div className="soil-sight-modal-subtitle">
                Delta (B minus A) per year — {location} / {rotation}
              </div>
            </div>
          ) : null
        }
        styles={{ body: { paddingTop: 8 } }}
      >
        {zoomed && <CompareChartContent metric={zoomed} chartData={chartData} scenarios={scenarios} height={420} fontSize={12} />}
      </AntModal>
    </div>
  );
};
